import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import React from 'react';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Drawer, TextField } from '@mui/material';
import { css } from '@emotion/react';
import { v4 as uuid } from 'uuid';
import api from '../../../../api';
import { color } from '@/styles';
import { useLoading } from '@/contexts/LoadingContext';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { OutlineButton } from '@/components/outlineButton';
import  { IGetResidentGasDetaSetsRes} from "@/types/api/bDevice/residentGasDataSets";
import { IGetNotificationRes } from '@/types/api/notification/getNotification';
import { IGetCareRecordRes } from '@/types/api/care-record/getCareRecord';
import { IGetResidentRes } from '@/types/api/resident/getResident';
import { IGetGasUltraSonicData, IGetGasUltraSonicDataRes} from "@/types/api/gas-data/getGasUltraSonicData";
import { DetailGraphItems } from './detailGraphItem';
import { NotifyAndCareRecordLabel} from "./NotifyAndCareRecordLabel";
import { getRecordNames, recordColors } from "./common";
import { Dataset } from './dataset';

interface Props {
  residentId: string;
}

interface ScatterItem {
  x: number;
  y: number;
  color: string;
  name: string;
}

type SensorKey = 'voc' | 'temp' | 'humidity';
type SensorReactionObject = [number, number | null][];
interface SensorConfig<T> extends Record<SensorKey, T> {}

type SensorReactions = SensorConfig<SensorReactionObject>;

const Container = css``;

const GraphContainer = css`
  padding: 8px 24px 24px;
  margin: 12px auto;
  z-index: 0;
`;

const GraphHeader = css`
  position: sticky;
  z-index: 1;
  top: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.17);
  padding: 24px 24px 8px;
  background: ${color.white};
`;

const ButtonContainer = css`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const DatePickerContainer = css`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;
`;

export const ResidentBenDetailGraph: React.FC<Props> = ({ residentId }) => {
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();
  const GAS_DATA_THRESHOLD = 86400; // sampling_rate 3secで1日28800 x 3days = dev環境でDynamoDBが落ちない負荷
  const DATASET_LOWER_THRESHOLD = 500; // datasetの件数がこの下限値以下であればdatasetとして表示しない
  const yRanges: SensorConfig<[number, number]> = {
    voc: [0, 10],
    temp: [10, 40],
    humidity: [0, 100],
  }
  const keyTitle: SensorConfig<string> = {
    voc: t('odor', 'におい'),
    temp: t('temperature', '温度') + ' (℃)',
    humidity: t('humidity', '湿度') + ' (%)',
  }
  const keyPlotColor: SensorConfig<string> = {
    voc: '#E7C25E',
    temp: '#53C847',
    humidity: '#53C847',
  }

  const recordNames = getRecordNames(t);

  const [range, setRange] = React.useState<{ start: dayjs.Dayjs; end: dayjs.Dayjs; isCreate: boolean }>({
    start: dayjs().local().set('hour', 0).set('minute', 0).set('second', 0).utc(),
    end: dayjs().local().add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0).utc(),
    isCreate: false,
  });
  const [charts, setCharts] = React.useState<
    {
      id: string;
      options: Highcharts.Options[];
      range: { start: dayjs.Dayjs; end: dayjs.Dayjs };
    }[]
  >([]);
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [datasets, setDataset] = React.useState<IGetResidentGasDetaSetsRes[]>();
  const [resident, setResident] = React.useState<IGetResidentRes>();

  const fetchDataset = React.useCallback(async () => {
    if (!range) return;
    showLoading();
    await Promise.all([
      await api.get('/b-devices/residents/:residentId/gas/data-sets', {
        params: { residentId, sort: 'desc', limit: 5000 },
      }),
    ]).then(([datasets]) => {
      // データセットドロワー部分で表示したいものだけにする
      const filterDatasets = datasets.filter(
        (v) =>
          v.number > DATASET_LOWER_THRESHOLD ||
          dayjs(v.endDateTime).isBetween(dayjs(new Date()).subtract(5, 'minutes'), dayjs(new Date()).add(5, 'minutes'))
      );
      setDataset(filterDatasets);
    });
    hideLoading();
  }, [range]);

  const handleCreateChart = React.useCallback((start: string, end: string) => {
    setRange({ start: dayjs(start).local().utc(), end: dayjs(end).local().utc(), isCreate: true });
  }, []);

  const handleUpdateChart = React.useCallback((start: string, end: string) => {
    setRange({ start: dayjs(start).local().utc(), end: dayjs(end).local().utc(), isCreate: false });
    handleCloseDrawer();
  }, []);

  const handleOpenDrawer = React.useCallback(() => {
    if (datasets && datasets.length > 0) {
      setDrawerOpen(true);
    } else {
      showSnackbar(t('noDataset', 'データセットがありません'));
    }
  }, [datasets]);

  const handleCloseDrawer = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleDeleteGraph = React.useCallback(
    (id: string) => {
      setCharts((prev) => {
        return prev.filter((v) => v.id !== id);
      });
    },
    [charts]
  );

  const yAxisLabelFormatter = React.useCallback(
    (value: any) => {
      return value.toString();
    },
    [charts]
  );

  const tooltipFormatter = React.useCallback(
    (x: any, y: any) => {
      const dateFormat = t('dayjsGraphDateTimeFormat', 'MM月DD日 HH時mm分');
      const localTime = dayjs(x).format(dateFormat);
      const yValue = y !== undefined ? y : '';
      return `${yValue}<br />${localTime}`;
    },
    [charts]
  );

  const clipAndNormalize = (value: number): number => {
    const maxClip = 300;
    const minClip = 50;
    if (value <= minClip) return 0;
    if (value >= maxClip) return 10;
    return ((value - minClip) / (maxClip - minClip)) * 10;
  };

  const convertRange = (value: number): number => {
    return Math.log2(value + 1)
  }

  const transformScatterData = (
      data: ScatterItem[],
      key: SensorKey,
      yRate: number = 0
  ): ScatterItem[] => {
    return data.map((item) => ({
      ...item,
      y: yRate === 0 ? yRanges[key][0] : yRanges[key][0] + (yRanges[key][1] - yRanges[key][0]) * yRate,
    }));
  };

  const createSensorReactionDatas = React.useCallback(
      (data: IGetGasUltraSonicDataRes): SensorReactions => {
        if (data.length === 0) {
          return {
            voc: [], temp: [], humidity: []
          };
        }

        const sensorData: SensorReactions = {
          voc: [],
          temp: [],
          humidity: []
        };

        const ONE_MINUTE = 60 * 1000;
        let prevTimestamp = Date.parse(data[0].timestamp);

        const addDataPoint = (timestamp: number, record: IGetGasUltraSonicData) => {
          sensorData.voc.push([timestamp, convertRange(record.voc)]);
          sensorData.temp.push([timestamp, record.temp]);
          sensorData.humidity.push([timestamp, record.humidity]);
        };

        const addNullPoint = (timestamp: number) => {
          Object.values(sensorData).forEach((array) => {
            array.push([timestamp, null]);
          });
        };

        addDataPoint(prevTimestamp, data[0]);

        data.slice(1).forEach((record) => {
          const timestamp = Date.parse(record.timestamp);
          const timeDiff = timestamp - prevTimestamp;

          if (timeDiff >= ONE_MINUTE) {
            if (timeDiff > ONE_MINUTE * 25) {
              addNullPoint(timestamp - ONE_MINUTE);
            }
            addDataPoint(timestamp, record);
            prevTimestamp = timestamp;
          }
        });

        return sensorData;

      },
      [residentId, range, resident]
  );

  const createNotificationScatter = React.useCallback(
    (data: IGetNotificationRes[]) => {
      const scatterData = data
        .filter((notification) => {
          const { notificationType } = notification;
          return notificationType === 'gasPooped' || notificationType === 'gasPrediction';
        }).map((notification) => {
          const { notificationType } = notification;
          let notificationLabel: string;
          let notificationColor: string;

          if (notificationType === 'gasPooped') {
            notificationLabel = t('odorDetection', '臭い検知');
            notificationColor = color.notification_orange_odor;
          } else {
            notificationLabel = t('estimatedTimeOfDefecation', '排便予測時間');
            notificationColor = color.notification_green;
          }

          return {
            x: Date.parse(notification.timestamp),
            y: 0,
            name: notificationLabel,
            color: notificationColor,
          };


          });
      return scatterData.length ? scatterData : [];
    },
    [residentId, range, resident]
  );

  const createCareRecordScatter = React.useCallback(
    (data: IGetCareRecordRes[]) => {
      const careRecordPlots = (data || []).filter((v: IGetCareRecordRes) => {
        const urineHasValue: boolean = v.padUrineVolumeType !== null && v.padUrineVolumeType !== 'none';
        const defecationHasValue: boolean = v.defecationVolumeType !== null && v.defecationVolumeType !== 'none';
        const noneChecked: boolean = v.padUrineVolumeType === 'none' && v.defecationVolumeType === 'none';


        return urineHasValue || defecationHasValue || noneChecked;
      }).map((v: IGetCareRecordRes) => {
        return {
          timestamp: v.timestamp,
          padUrine: v.padUrineVolumeType !== 'none' && !!v.padUrineVolumeType,
          defecation: v.defecationVolumeType !== 'none' && !!v.defecationVolumeType,
        };
      });

      const scatterData = (careRecordPlots || []).map((data) => {
        const { padUrine, defecation, timestamp } = data;
        const recordType = defecation && padUrine ? 'defecationAndUrine' : defecation ? 'defecation' : padUrine ? 'urine' : 'none';
        return {
          x: Date.parse(timestamp),
          y: 10,
          name: recordNames[recordType],
          color: recordColors[recordType],
        };
      });

      return scatterData.length ? scatterData : [];
    },
    [residentId, range, resident]
  );

  const fetchCharts = React.useCallback(() => {
    const params = {
      residentId,
      from: formatUtcFetchTimestamp(range.start),
      to: formatUtcFetchTimestamp(range.end),
      sort: 'asc',
    };
    Promise.all([
      api.get('/residents/:residentId', { params: { residentId } }),
      api.get('/b-devices/residents/:residentId/gas/ultra-sonic-data', { params: { ...params, limit: GAS_DATA_THRESHOLD } }),
      api.get('/residents/:residentId/notifications', { params: { ...params, limit: 10000, notificationClass: 1 } }),
      api.get('/residents/:residentId/care-records', { params: { ...params, limit: 10000 } }),
    ]).then(([resident, sensorReactions, notifications, records]) => {
      const sensorReactionDatas = createSensorReactionDatas(sensorReactions);
      const notificationScatter = createNotificationScatter(notifications);
      const careRecordScatter = createCareRecordScatter(records);

      setResident(resident);
      setOption(sensorReactionDatas, notificationScatter, careRecordScatter);
    });
  }, [residentId, range, resident]);

  const setOption = React.useCallback(
    (
      sensorDatas: SensorReactions,
      notificationData: ScatterItem[],
      careRecordData: ScatterItem[],
    ) => {
      const options: Highcharts.Options[] = Object.entries(sensorDatas).map(([key, sensorData]: [string, SensorReactionObject]) => {
        const typedKey = key as SensorKey;
        return {
          chart: {
            zoomType: 'x',
                height: 180,
          },
          time: {
            timezoneOffset: new Date().getTimezoneOffset(),
          },
          title: {
            text: '',
          },
          xAxis: {
            type: 'datetime',
            min: Date.parse(range.start.format()),
            max: Date.parse(range.end.format()),
            showEmpty: true,
            opposite: false,
            gridLineWidth: 1,
            lineWidth: 0,
            tickWidth: 0,
            minPadding: 0,
            maxPadding: 0,
            plotLines: [
                {
                  color: '#CCCCCC',
                  dashStyle: 'Solid',
                  value: Date.parse(range.start.format()),
                  width: 0.5,
                },
                {
                  color: '#CCCCCC',
                  dashStyle: 'Solid',
                  value: Date.parse(range.end.format()),
                  width: 0.5,
                },
            ],
            dateTimeLabelFormats: {
              minute: '%H:%M',
              hour: '%H:00',
              day: t('graphDayFormat', '%m月%d日'),
              week: t('graphDayFormat', '%m月%d日'),
              month: t('graphYearFormat', '%m月%d日'),
            },
            crosshair: {
              width: 2,
            },
          },
          yAxis: {
            title: {
              text: keyTitle[typedKey],
            },
            labels: {
              formatter: function () {
                return yAxisLabelFormatter(this.value);
              },
            },
            min: yRanges[typedKey][0],
            max: yRanges[typedKey][1],
            tickInterval: 10,
            crosshair: {
              width: 2,
            },
          },
          legend: {
            enabled: false,
          },
          plotOptions: {
            series: {
              marker: {
                enabled: false,
              },
            },
            scatter: {
              marker: {
                enabled: true,
              },
              states: {
                hover: {
                  enabled: true,
                },
              },
            },
          },
          series: [
            {
              name: keyTitle[typedKey],
              data: sensorData,
              yAxis: 0,
              color: keyPlotColor[typedKey],
              zIndex: 1,
              type: 'area',
              lineWidth: 3,
              fillOpacity: 0.5,
              tooltip: {
                headerFormat: '<b>{series.name}</b><br>',
                pointFormatter: function () {
                  return tooltipFormatter(this.x, this.y);
                },
              },
            },
            {
              name: t('notification', '通知'),
              marker: {
                enabled: true,
                symbol: 'circle',
              },
              zIndex: 3,
              data: transformScatterData(notificationData, typedKey, 0),
              type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
              lineWidth: 0,
              states: {
                hover: {
                  lineWidth: 0,
                  lineWidthPlus: 0,
                },
              },
              tooltip: {
                headerFormat: '<b>{series.name}</b><br>',
                pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
              },
            },
            {
              name: t('careHistory', 'ケア履歴'),
              marker: {
                enabled: true,
                symbol: 'square',
              },
              zIndex: 3,
              data: transformScatterData(careRecordData, typedKey, 0.1),
              type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
              lineWidth: 0,
              states: {
                hover: {
                  lineWidth: 0,
                  lineWidthPlus: 0,
                },
              },
              tooltip: {
                headerFormat: '<b>{series.name}</b><br>',
                pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
              },
            },
          ],
          credits: {
            enabled: false,
          },
        }
      });

      if (range.isCreate) {
        setCharts([
          ...charts,
          {
            id: uuid(),
            options,
            range,
          },
        ]);
      } else {
        setCharts((prev) => {
          if (!prev[0]) {
            return [{ id: uuid(), options, range }];
          }
          prev[0].options = options;
          return [...prev];
        });
      }
    },
    [residentId, range, resident]
  );

  const handleChangeStart = React.useCallback((start) => {
    setRange((prev) => {
      return {
        ...prev,
        start: dayjs(start).local().utc(),
        isCreate: false,
      };
    });
  }, []);

  const handleChangeEnd = React.useCallback((end) => {
    setRange((prev) => {
      return {
        ...prev,
        end: dayjs(end).local().utc(),
        isCreate: false,
      };
    });
  }, []);

  React.useEffect(() => {
    if (!range) return;
    (async () => {
      await fetchDataset();
    })();
    fetchCharts();
  }, [range]);

  return (
    <div css={Container}>
      <div css={GraphHeader}>
        <div css={ButtonContainer}>
          <OutlineButton onClick={handleOpenDrawer}>
            <img src='/images/residents/dataset.svg' />
            {t('dataset', 'データセット')}
          </OutlineButton>
        </div>
        <NotifyAndCareRecordLabel />
      </div>

      <div css={GraphContainer}>
        <div css={DatePickerContainer}>
          <DateTimePicker
            ampm={false}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            value={range.start.local()}
            maxDate={range.end.local()}
            onChange={handleChangeStart}
            renderInput={(params) => <TextField variant={'standard'} {...params} helperText={null} />}
          />
          〜
          <DateTimePicker
            ampm={false}
            views={['year', 'month', 'day', 'hours', 'minutes']}
            value={range.end.local()}
            minDate={range.start.local()}
            maxDate={dayjs().local().add(1, 'day').set('hour', 0).set('minute', 0).set('second', 0)}
            onChange={handleChangeEnd}
            renderInput={(params) => <TextField variant={'standard'} {...params} helperText={null} />}
          />
        </div>
        {charts.length > 0 && (
          <>
            {charts.map((chart, index) => (
              <DetailGraphItems
                key={chart.id}
                id={chart.id}
                graphOptions={ chart.options }
                range={chart.range}
                isFirst={index === 0}
                onDelete={handleDeleteGraph}
              />
            ))}
          </>
        )}
      </div>
      {isDrawerOpen && datasets && (
        <>
          {datasets.length > 0 && (
            <Drawer sx={{ position: 'relative' }} anchor='right' open={isDrawerOpen} onClose={handleCloseDrawer}>
              <Dataset
                datasets={datasets}
                addGraph={handleCreateChart}
                updateGraph={handleUpdateChart}
                onClose={handleCloseDrawer}
              />
            </Drawer>
          )}
        </>
      )}
    </div>
  );
};
